import React, { useMemo } from 'react';
import ReactWordcloud from 'react-wordcloud';
import { interpolateRgb } from 'd3-interpolate';
import { buildWordCloudData } from '../../utils/dashboardAssessmentAdapter';
import type { DashboardAssessmentData } from '../../utils/dashboardAssessmentAdapter';
import ContentLoader from '../../components/ContentLoader';
import '../../assets/css/DashboardVisualizations.css';

interface DashboardVisualizationsProps {
  assessmentData: DashboardAssessmentData | null;
  loading?: boolean;
  error?: string | null;
}

const DashboardVisualizations: React.FC<DashboardVisualizationsProps> = ({
  assessmentData,
  loading = false,
  error = null,
}) => {
  const wordCloudData = useMemo(
    () =>
      buildWordCloudData(assessmentData?.rows ?? [])
        .sort((a, b) => b.value - a.value)
        .slice(0, 35),
    [assessmentData]
  );

  const wordCloudColors = useMemo(() => {
    const steps = 16;
    const interp = interpolateRgb('rgb(15, 107, 102)', 'rgb(32, 48, 46)');
    const accentInterp = interpolateRgb('rgb(254, 122, 89)', 'rgb(154, 74, 50)');
    const teal = Array.from({ length: steps / 2 }, (_, index) =>
      interp(index / (steps / 2 - 1))
    );
    const coral = Array.from({ length: steps / 2 }, (_, index) =>
      accentInterp(index / (steps / 2 - 1))
    );
    return [...teal, ...coral];
  }, []);

  const wordCloudOptions = {
    rotations: 1,
    rotationAngles: [0] as [number],
    fontSizes: [32, 14] as [number, number],
    padding: 4,
    margin: [16, 16, 16, 16] as [number, number, number, number],
    colors: wordCloudColors,
    deterministic: true,
    transitionDuration: 300,
  };

  return (
    <div className="dash-chart-block">
      {loading && <ContentLoader compact label="Loading data…" />}
      {error && <p className="dash-chart-error">{error}</p>}

      {!loading && !error && (
        <div className="dash-your-project">
          <h2 className="dash-chart-title app-heading-accent">Assessment themes</h2>
          {wordCloudData.length > 0 ? (
            <div className="my-wordcloud-container">
              <ReactWordcloud words={wordCloudData} options={wordCloudOptions} />
            </div>
          ) : (
            <p className="dash-chart-status">
              Add assessment comments to generate the word cloud.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardVisualizations;
